import React, { useRef } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { ScrollAnimation } from './ScrollAnimation';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { PageHeroTitle } from '../styles/typography';
import { useDecorativeVideo } from '../hooks/useDecorativeVideo';
import FAQ, { FAQItemData } from './FAQ';
import { ProofMark } from './ProofBasis';
import ProofBand from './ProofBand';
import StickyCtaBar, { StickyCtaBarProps } from './StickyCtaBar';

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
`;

const gradientMove = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

// Video background styled components (matching homepage settings)
const VideoWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  z-index: 0;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 30%;
    background: linear-gradient(to bottom, transparent, var(--color-primary-light));
    z-index: 2;
  }
`;

const VideoBackground = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
  opacity: 0.4;
  filter: blur(2px);
`;

const VideoOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.7) 0%,
    rgba(245, 247, 250, 0.6) 100%
  );
  z-index: 1;
`;

// Styled Components
const PageWrapper = styled.div<{ $heroImage?: string; $banded?: boolean }>`
  padding: var(--spacing-section-mobile) 0;
  background: linear-gradient(180deg, 
    var(--color-primary-light) 0%, 
    var(--color-primary-base) 30%,
    var(--color-primary-light) 100%);
  min-height: 100vh;
  position: relative;
  overflow: hidden;

  @media (min-width: 768px) {
    padding: var(--spacing-section) 0;
  }

  /* Banded layout: tighter page padding, one rhythm variable for the gaps between
     blocks, and overflow: clip (not hidden) so a sticky child can still stick. */
  ${props => props.$banded && css`
    --band-gap: 44px;
    /* The top padding clears the fixed header: the page sits under 80px (90px from 768px) of
       main padding, and the header is 96px (117px) tall. */
    padding: 44px 0 var(--band-gap);
    overflow: hidden;
    overflow: clip;

    @media (min-width: 768px) {
      --band-gap: 68px;
      padding: 48px 0 var(--band-gap);
    }
  `}

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    ${props => {
      if (!props.$heroImage) {
        return `
          background-image:
            radial-gradient(circle at 20% 80%, rgba(11,152,206,0.03) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(49,110,168,0.04) 0%, transparent 50%);
        `;
      }
      // Normalize .png refs to the optimized .webp; sibling .avif plus
      // -tablet / -mobile variants are produced by
      // frontend/scripts/optimize-page-images.js. image-set() lets the
      // browser pick AVIF; the plain url() before it keeps pre-Safari-17
      // browsers on the WebP path. @media gates pick the responsive
      // variant — tablet ≤1280, mobile ≤768.
      const webp = props.$heroImage.replace('.png', '.webp');
      const avif = webp.replace(/\.webp$/, '.avif');
      const tabletWebp = webp.replace(/\.webp$/, '-tablet.webp');
      const tabletAvif = webp.replace(/\.webp$/, '-tablet.avif');
      const mobileWebp = webp.replace(/\.webp$/, '-mobile.webp');
      const mobileAvif = webp.replace(/\.webp$/, '-mobile.avif');
      const gradients =
        `radial-gradient(circle at 20% 80%, rgba(11,152,206,0.03) 0%, transparent 50%),
           radial-gradient(circle at 80% 20%, rgba(49,110,168,0.04) 0%, transparent 50%)`;
      return `
        background-image: url('${webp}'), ${gradients};
        background-image: image-set(url('${avif}') type('image/avif'), url('${webp}') type('image/webp')), ${gradients};

        @media (max-width: 1280px) {
          background-image: url('${tabletWebp}'), ${gradients};
          background-image: image-set(url('${tabletAvif}') type('image/avif'), url('${tabletWebp}') type('image/webp')), ${gradients};
        }

        @media (max-width: 768px) {
          background-image: url('${mobileWebp}'), ${gradients};
          background-image: image-set(url('${mobileAvif}') type('image/avif'), url('${mobileWebp}') type('image/webp')), ${gradients};
        }
      `;
    }}
    background-size: cover, auto, auto;
    background-position: center, 20% 80%, 80% 20%;
    background-repeat: no-repeat, no-repeat, no-repeat;
    opacity: ${props => props.$heroImage ? '0.08' : '1'};
    pointer-events: none;
    display: block;
  }
`;

const HeroSection = styled.section<{ $banded?: boolean }>`
  text-align: center;
  margin-bottom: var(--spacing-4xl);
  position: relative;
  z-index: 2;

  ${props => props.$banded && css`
    margin-bottom: 64px;

    /* Wide desktops: keep the hero and the proof band short enough that the next
       section's eyebrow and the start of its heading show below the band on a
       900px-tall screen, so the band is never mistaken for the end of the page. */
    @media (min-width: 768px) {
      margin-bottom: 20px;
    }

    h1 {
      margin-bottom: var(--spacing-sm);
    }
  `}
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-sm) var(--spacing-md);
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(11,152,206,0.1);
  border-radius: 999px;
  color: var(--text-secondary);
  text-decoration: none;
  font-size: var(--fs-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: var(--spacing-xl);
  
  &:hover {
    background: white;
    border-color: var(--color-accent-primary);
    color: var(--color-accent-primary);
    transform: translateX(-4px);
  }
  
  svg {
    width: 16px;
    height: 16px;
    transform: rotate(180deg);
  }
`;

const ServiceBadge = styled.div<{ $banded?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-xs) var(--spacing-md);
  background: rgba(11,152,206,0.1);
  border: 1px solid rgba(11,152,206,0.2);
  border-radius: 999px;
  color: var(--color-accent-primary);
  font-size: var(--fs-sm);
  font-weight: var(--font-weight-medium);
  margin-bottom: var(--spacing-lg);
  
  svg {
    width: 16px;
    height: 16px;
  }

  /* Midnight text: Davinci Blue on the page's grey wash is about 3:1. */
  ${props => props.$banded && css`
    margin-bottom: 0.75rem;
    color: var(--color-accent-dark);
    font-weight: var(--font-weight-semibold);

    svg {
      color: var(--color-accent-primary-fill);
    }
  `}
`;

const Title = PageHeroTitle;

const Subtitle = styled.p<{ $banded?: boolean }>`
  font-size: var(--fs-xl);
  color: var(--text-secondary);
  max-width: 800px;
  margin: 0 auto var(--spacing-xl);
  line-height: var(--lh-relaxed);

  ${props => props.$banded && css`
    max-width: 680px;
    margin: 0.75rem auto 0;
    font-size: 1.05rem;
    line-height: var(--lh-snug);

    @media (min-width: 768px) {
      max-width: 780px;
      font-size: var(--fs-lg);
    }
  `}
`;

/* The price line under a banded hero's lead: one bold sentence, then small print. */
const HeroPrice = styled.p<{ $balance?: boolean }>`
  max-width: 680px;
  margin: 0.9rem auto 0;
  font-size: var(--fs-lg);
  font-weight: var(--font-weight-bold);
  line-height: var(--lh-snug);
  color: var(--color-accent-dark);
  /* Opt-in (heroPrice.balance): a longer line, like the AI page's discovery sentence, breaks evenly instead of
     leaving one word on the last row. Off by default, so the frozen managed IT hero wraps exactly as it did. */
  ${({ $balance }) => $balance && 'text-wrap: balance;'}

  small {
    display: block;
    margin-top: 0.15rem;
    font-size: max(13px, var(--fs-xs));
    font-weight: var(--font-weight-medium);
    color: var(--text-primary);
  }
`;

const HeroActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.8rem;
  margin-top: 1.1rem;

  a,
  button {
    width: 100%;
    justify-content: center;
    min-height: 52px;
    padding: 0.75rem 1.6rem;
  }

  @media (min-width: 600px) {
    flex-direction: row;
    justify-content: center;
    gap: 1.4rem;

    a,
    button {
      width: auto;
      min-width: 340px;
    }
  }
`;

/* Midnight, not Davinci Blue: blue text on the page's grey wash is under 4.5:1. */
const HeroSecondaryLink = styled(Link)`
  font-weight: var(--font-weight-semibold);
  color: var(--color-accent-dark);
  text-decoration: underline;
  text-decoration-thickness: 2px;
  text-underline-offset: 3px;

  ${HeroActions} & {
    width: auto;
    min-width: 0;
    min-height: 0;
    padding: 0;
  }
`;

const HighlightStats = styled.div`
  display: inline-flex;
  gap: var(--spacing-xl);
  padding: var(--spacing-md) var(--spacing-xl);
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px);
  border-radius: 999px;
  box-shadow: var(--shadow-md);
  border: 1px solid rgba(11,152,206,0.1);
  margin-top: var(--spacing-sm);

  @media (max-width: 640px) {
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: var(--spacing-sm);
    border-radius: 24px;
    padding: var(--spacing-md) var(--spacing-md);
  }
`;

const Stat = styled.div`
  text-align: center;
  
  .value {
    font-size: var(--fs-xl);
    font-weight: var(--font-weight-bold);
    color: var(--color-accent-primary);
    display: block;
  }
  
  .label {
    font-size: var(--fs-xs);
    color: var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: var(--ls-wider);
  }
`;

/* Basis or period for a hero figure (e.g. the organisations count), small but visible. */
const StatNote = styled.small`
  display: block;
  margin: var(--spacing-sm) auto 0;
  max-width: 560px;
  font-size: var(--fs-xs);
  line-height: var(--lh-snug);
  color: var(--text-secondary);
  text-align: center;
`;

const ContentSection = styled.section`
  position: relative;
  z-index: 2;
  margin-bottom: var(--spacing-4xl);
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: var(--spacing-3xl);
  
  h2 {
    font-size: var(--fs-3xl);
    color: var(--text-primary);
    margin-bottom: var(--spacing-md);
  }
  
  p {
    font-size: var(--fs-lg);
    color: var(--text-secondary);
    max-width: 600px;
    margin: 0 auto;
    line-height: var(--lh-relaxed);
  }
`;

const ContentGrid = styled.div<{ $columns?: number }>`
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--spacing-xl);
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(${props => props.$columns === 2 ? 2 : 1}, 1fr);
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: repeat(${props => props.$columns || 3}, 1fr);
  }
`;

const FeatureCard = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: var(--spacing-xl);
  border: 1px solid rgba(11,152,206,0.1);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  animation: ${fadeIn} 0.6s ease-out;
  
  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(135deg, 
      var(--color-accent-primary), 
      var(--color-accent-secondary),
      var(--color-accent-primary));
    background-size: 200% 200%;
    animation: ${gradientMove} 8s ease infinite;
    border-radius: 20px;
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: -1;
  }
  
  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 0 20px 60px rgba(11,152,206,0.15);
    
    &::before {
      opacity: 0.3;
    }
    
    .icon {
      animation: ${float} 2s ease-in-out infinite;
    }
  }
  
  .icon {
    width: 48px;
    height: 48px;
    color: var(--color-accent-primary);
    margin-bottom: var(--spacing-lg);
    transition: all 0.3s ease;
  }
  
  h3 {
    font-size: var(--fs-xl);
    color: var(--text-primary);
    margin-bottom: var(--spacing-md);
    font-weight: var(--font-weight-bold);
  }
  
  p {
    color: var(--text-secondary);
    line-height: var(--lh-relaxed);
    margin-bottom: var(--spacing-md);
  }
  
  .benefits {
    list-style: none;
    padding: 0;
    margin: 0;
    
    li {
      display: flex;
      align-items: center;
      gap: var(--spacing-xs);
      color: var(--text-secondary);
      font-size: var(--fs-sm);
      margin-bottom: var(--spacing-xs);
      
      &:before {
        content: '✓';
        color: var(--color-accent-primary);
        font-weight: var(--font-weight-bold);
        font-size: var(--fs-md);
      }
    }
  }
`;

const ProcessSection = styled.section`
  position: relative;
  z-index: 2;
  margin-bottom: var(--spacing-4xl);
`;

const ProcessTimeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2xl);
  max-width: 800px;
  margin: 0 auto;
  
  @media (min-width: 768px) {
    gap: var(--spacing-xl);
  }
`;

const ProcessStep = styled.div<{ $index: number }>`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-lg);
  animation: ${fadeIn} 0.6s ease-out;
  animation-delay: ${props => props.$index * 0.1}s;
  animation-fill-mode: both;
  
  @media (max-width: 767px) {
    flex-direction: column;
    text-align: center;
    gap: var(--spacing-md);
  }
`;

const StepNumber = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--color-accent-primary-fill), var(--color-accent-secondary));
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--fs-xl);
  font-weight: var(--font-weight-bold);
  flex-shrink: 0;
  box-shadow: 0 4px 20px rgba(11,152,206,0.3);
`;

const StepContent = styled.div`
  flex: 1;
  
  h3 {
    font-size: var(--fs-xl);
    color: var(--text-primary);
    margin-bottom: var(--spacing-sm);
    font-weight: var(--font-weight-bold);
  }
  
  p {
    color: var(--text-secondary);
    line-height: var(--lh-relaxed);
  }
`;

const CTASection = styled.section`
  text-align: center;
  padding: var(--spacing-3xl) var(--spacing-xl);
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  border: 1px solid rgba(11,152,206,0.1);
  box-shadow: 0 20px 60px rgba(0,0,0,0.08);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, 
      rgba(11,152,206,0.02) 0%, 
      rgba(49,110,168,0.03) 100%);
    pointer-events: none;
  }
  
  h2 {
    font-size: var(--fs-2xl);
    color: var(--text-primary);
    margin-bottom: var(--spacing-md);
    position: relative;
    z-index: 1;
  }
  
  p {
    font-size: var(--fs-lg);
    color: var(--text-secondary);
    margin-bottom: var(--spacing-xl);
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
    position: relative;
    z-index: 1;
  }
`;

const ctaStyles = css`
  padding: var(--spacing-md) var(--spacing-2xl);
  background: linear-gradient(135deg, var(--color-accent-primary-fill), var(--color-accent-secondary));
  color: white;
  border: none;
  border-radius: 12px;
  font-size: var(--fs-md);
  font-weight: var(--font-weight-bold);
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-sm);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(11,152,206,0.3);
  }
  
  svg {
    width: 20px;
    height: 20px;
    transition: transform 0.3s ease;
  }
  
  &:hover svg {
    transform: translateX(4px);
  }

  @media (max-width: 768px) {
    svg {
      display: none;
    }
  }
`;

const CTAButton = styled.button`
  ${ctaStyles}
`;

// A real <a href>: crawlers follow it, and Cmd/Ctrl-click opens a new tab.
const CTALink = styled(Link)`
  ${ctaStyles}
  text-decoration: none;
  color: white;

  &:hover {
    color: white;
  }
`;

const CTAExtras = styled.div`
  margin-top: var(--spacing-lg);
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-sm);
  color: var(--text-secondary);

  /* --color-accent-primary-fill (#0A7DAA) is the AA-contrast blue for text on
     white; the lighter accent is about 3.3:1 and fails for body-size links. */
  a {
    color: var(--color-accent-primary-fill);
    font-weight: var(--font-weight-medium);
    text-decoration: underline;
    text-underline-offset: 2px;
  }
`;

const Lead = styled.p`
  max-width: 820px;
  margin: 0 auto var(--spacing-3xl);
  padding: var(--spacing-lg) var(--spacing-xl);
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  border-left: 4px solid var(--color-accent-primary-fill);
  color: var(--text-primary);
  font-size: var(--fs-lg);
  line-height: var(--lh-relaxed);
  position: relative;
  z-index: 2;
`;

const ProofStrip = styled.section`
  position: relative;
  z-index: 2;
  max-width: 1000px;
  margin: 0 auto var(--spacing-4xl);
`;

const ProofTiles = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--spacing-md);

  @media (min-width: 900px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const ProofTile = styled.li`
  text-align: center;
  padding: var(--spacing-lg) var(--spacing-md);
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  border: 1px solid rgba(11,152,206,0.1);
  box-shadow: var(--shadow-sm);

  .value {
    display: block;
    font-size: var(--fs-2xl);
    font-weight: var(--font-weight-bold);
    color: var(--color-accent-primary);
  }

  .label {
    display: block;
    margin-top: var(--spacing-xs);
    font-size: var(--fs-sm);
    color: var(--text-secondary);
    line-height: var(--lh-normal);
  }
`;

const ProofFootnotes = styled.ol`
  margin: var(--spacing-lg) 0 0;
  padding-left: var(--spacing-xl);
  font-size: var(--fs-xs);
  line-height: var(--lh-relaxed);
  color: var(--text-secondary);

  li {
    margin-bottom: var(--spacing-xs);
  }

  strong {
    color: var(--text-primary);
  }
`;

/* Everything under the hero on a banded page: one flex column, so the gap between
   blocks is a single variable (44px on phones, 68px from 768px). */
const BandedStack = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: var(--band-gap);
`;

const ProofSlot = styled.div`
  position: relative;
  z-index: 2;
  margin-bottom: var(--band-gap);

  /* Tighter than the gap between the other blocks: see HeroSection. */
  @media (min-width: 768px) {
    margin-bottom: 28px;
  }
`;

const CompactCTA = styled.section`
  position: relative;
  z-index: 2;
  display: grid;
  gap: 16px;
  align-items: center;
  padding: 24px 18px;
  text-align: center;
  background: var(--white);
  border: 1px solid rgba(11, 152, 206, 0.14);
  border-radius: 20px;
  box-shadow: 0 6px 20px rgba(20, 53, 98, 0.08);

  h2 {
    margin: 0;
    font-size: var(--fs-xl);
    font-weight: var(--font-weight-extra-bold);
    line-height: var(--lh-snug);
    color: var(--color-accent-dark);
  }

  p {
    margin: 0.4rem 0 0;
    font-size: var(--fs-sm);
    line-height: var(--lh-normal);
    color: var(--text-primary);
  }

  @media (min-width: 900px) {
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 30px;
    padding: 30px 36px;
    text-align: left;
  }
`;

const CompactActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;

  a,
  button {
    width: 100%;
    justify-content: center;
    min-height: 52px;
    padding: 0.75rem 1.6rem;
  }

  @media (min-width: 900px) {
    align-items: flex-end;

    a,
    button {
      width: auto;
      min-width: 330px;
    }
  }
`;

const CompactExtras = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 0 0.5rem;
  font-size: var(--fs-sm);
  color: var(--text-secondary);

  /* The AA-contrast blue for text on white, underlined so it is not told apart by colour alone. */
  a {
    width: auto;
    min-width: 0;
    min-height: 0;
    padding: 0;
    color: var(--color-accent-primary-fill);
    text-decoration: underline;
    text-underline-offset: 2px;
  }
`;

// Interface

/**
 * A call to action. With `href` it renders a real anchor, so Googlebot sees the
 * link and Cmd/Ctrl-click works; the optional `onClick` carries analytics.
 * Without `href` it renders a button, for actions that open a modal rather than
 * go anywhere.
 */
export type ServiceCtaAction =
  | {
      buttonText: string;
      href: string;
      /** Full page load, for server-side redirects such as /book. */
      reloadDocument?: boolean;
      onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
      onAction?: never;
    }
  | {
      buttonText: string;
      onAction: () => void;
      href?: never;
      reloadDocument?: never;
      onClick?: never;
    };

export interface ServiceProofTile {
  value: string;
  label: string;
}

export interface ServiceProofFootnote {
  /** The figure the note defines, e.g. "22 min". */
  term: string;
  text: string;
}

/**
 * The call to action in the phone-only sticky bar (banded layout). It is shown
 * once the hero CTA has scrolled away and hidden while the closing CTA is on
 * screen, so it carries its own analytics handlers.
 */
export type ServiceStickyCta = Omit<StickyCtaBarProps, 'heroRef' | 'endRef'>;

interface ServiceLayoutProps {
  serviceName: string;
  icon: React.ReactNode;
  title: string;
  subtitle: React.ReactNode;
  heroImage?: string;
  heroVideo?: string;
  hideBackLink?: boolean;
  hideServiceBadge?: boolean;
  /**
   * `banded` (round 27) is the dense page: a tighter hero with a price line, the
   * proof tiles as a dark band with their definitions folded into a disclosure,
   * one rhythm between blocks, a split FAQ and a compact closing card. It is
   * opt-in; every other page keeps the default cards.
   */
  layout?: 'default' | 'banded';
  /** Banded hero: one bold price sentence with optional small print under it. */
  heroPrice?: { line: React.ReactNode; note?: React.ReactNode; /** Break the line evenly (opt-in; default is the browser's normal wrap). */ balance?: boolean };
  /** Banded hero: a quiet link beside the primary button, e.g. "See pricing". */
  heroSecondary?: { text: string; href: string; onClick?: () => void };
  /** Banded layout: the phone-only sticky call-to-action bar. */
  stickyCta?: ServiceStickyCta;
  /** Answer-first paragraph shown straight under the hero. */
  lead?: React.ReactNode;
  stats?: Array<{ value: string; label: string; note?: string }>;
  /**
   * Proof tiles with visible footnotes. The footnote at the same index defines
   * the tile, so the reader can see what each figure measures.
   */
  proofStrip?: {
    tiles: ServiceProofTile[];
    footnotes?: ServiceProofFootnote[];
    /** Banded layout: the band's heading, and the basis line shown inside its definitions panel. */
    heading?: string;
    basis?: string;
    /** Banded layout: a link at the end of the definitions panel to the full definitions page. */
    moreLink?: { text: string; href: string };
  };
  features?: Array<{
    icon: React.ReactNode;
    title: string;
    description: string;
    benefits?: string[];
  }>;
  /** Heading above the feature cards; defaults to "Service Offerings". */
  featuresHeading?: { title: string; description: string };
  process?: {
    title: string;
    description: string;
    steps: Array<{ title: string; description: string }>;
  };
  heroCta?: ServiceCtaAction;
  cta: ServiceCtaAction & {
    title: string;
    description: string;
    /** A second, quieter link under the button, e.g. "See pricing". */
    secondary?: { text: string; href: string; onClick?: () => void };
    /** Free-form line under the button, e.g. a phone number. */
    footer?: React.ReactNode;
  };
  /** Visible FAQ, rendered after `children` and before the bottom CTA. Banded layout adds the eyebrow and aside. */
  faqs?: {
    heading: string;
    intro?: string;
    items: FAQItemData[];
    /** Banded layout: show this many questions; the rest sit behind "More questions", still in the HTML. */
    initialVisible?: number;
    eyebrow?: string;
    aside?: React.ReactNode;
    /** Called when a visitor expands a question; for analytics. */
    onOpen?: (item: FAQItemData, index: number) => void;
  };
  children?: React.ReactNode;
}

const CtaAction: React.FC<ServiceCtaAction> = ({ buttonText, href, reloadDocument, onClick, onAction }) => {
  if (href !== undefined) {
    return (
      <CTALink to={href} reloadDocument={reloadDocument} onClick={onClick} data-cta="primary">
        {buttonText}
        <ArrowRightIcon />
      </CTALink>
    );
  }
  return (
    <CTAButton onClick={onAction} data-cta="primary">
      {buttonText}
      <ArrowRightIcon />
    </CTAButton>
  );
};

const ServiceLayout: React.FC<ServiceLayoutProps> = ({
  serviceName,
  icon,
  title,
  subtitle,
  heroImage,
  heroVideo,
  hideBackLink,
  hideServiceBadge,
  layout = 'default',
  heroPrice,
  heroSecondary,
  stickyCta,
  lead,
  stats,
  proofStrip,
  features = [],
  featuresHeading,
  process,
  heroCta,
  cta,
  faqs,
  children
}) => {
  const showDecorativeVideo = useDecorativeVideo();
  const banded = layout === 'banded';
  const heroCtaRef = useRef<HTMLDivElement>(null);
  const closingCtaRef = useRef<HTMLElement>(null);
  const Flow: React.ElementType = banded ? BandedStack : React.Fragment;

  return (
    <PageWrapper $heroImage={heroImage} $banded={banded}>
      {heroVideo && showDecorativeVideo && (
        <VideoWrapper>
          <VideoBackground autoPlay loop muted playsInline>
            <source src={heroVideo} type="video/webm" />
          </VideoBackground>
          <VideoOverlay />
        </VideoWrapper>
      )}
      <div className="container">
        <HeroSection $banded={banded}>
          <ScrollAnimation animation="fadeIn" delay={100}>
            {!hideBackLink && (
              <BackLink to="/services">
                <ArrowRightIcon />
                Back to Services
              </BackLink>
            )}

            {!hideServiceBadge && (
              <ServiceBadge $banded={banded}>
                {icon}
                {serviceName}
              </ServiceBadge>
            )}

            <Title>{title}</Title>
            <Subtitle $banded={banded}>{subtitle}</Subtitle>
            {banded && heroPrice && (
              <HeroPrice $balance={heroPrice.balance}>
                {heroPrice.line}
                {heroPrice.note && <small>{heroPrice.note}</small>}
              </HeroPrice>
            )}

            {heroCta && banded && (
              <HeroActions ref={heroCtaRef}>
                <CtaAction {...heroCta} />
                {heroSecondary && (
                  <HeroSecondaryLink to={heroSecondary.href} onClick={heroSecondary.onClick}>{heroSecondary.text}</HeroSecondaryLink>
                )}
              </HeroActions>
            )}
            {heroCta && !banded && (
              <div ref={heroCtaRef} style={{ marginTop: 'var(--spacing-xl)' }}>
                <CtaAction {...heroCta} />
              </div>
            )}

            {!heroCta && stats && (
              <HighlightStats>
                {stats.map((stat, index) => (
                  <Stat key={index}>
                    <span className="value">{stat.value}{stat.note && <ProofMark />}</span>
                    <span className="label">{stat.label}</span>
                  </Stat>
                ))}
              </HighlightStats>
            )}
            {!heroCta && stats && stats.filter((stat) => stat.note).map((stat) => (
              <StatNote key={stat.label} data-stat-note>*{stat.note}</StatNote>
            ))}
          </ScrollAnimation>
        </HeroSection>

        {lead && <Lead>{lead}</Lead>}

        {proofStrip && banded && (
          <ProofSlot>
            <ProofBand
              heading={proofStrip.heading ?? 'Measured results'}
              basis={proofStrip.basis}
              moreLink={proofStrip.moreLink}
              tiles={proofStrip.tiles}
              footnotes={proofStrip.footnotes}
            />
          </ProofSlot>
        )}

        {proofStrip && !banded && (
          <ProofStrip aria-label="Measured results">
            <ProofTiles>
              {proofStrip.tiles.map((tile, index) => (
                <ProofTile key={tile.label}>
                  <span className="value">{tile.value}</span>
                  <span className="label">
                    {tile.label}
                    {proofStrip.footnotes?.[index] && <sup>{index + 1}</sup>}
                  </span>
                </ProofTile>
              ))}
            </ProofTiles>
            {proofStrip.footnotes && proofStrip.footnotes.length > 0 && (
              <ProofFootnotes>
                {proofStrip.footnotes.map((note) => (
                  <li key={note.term}>
                    <strong>{note.term}.</strong> {note.text}
                  </li>
                ))}
              </ProofFootnotes>
            )}
          </ProofStrip>
        )}

        {heroCta && stats && (
          <ScrollAnimation animation="fadeIn" delay={200}>
            <div style={{ textAlign: 'center' }}>
            <HighlightStats style={{ marginBottom: stats.some((stat) => stat.note) ? 0 : 'var(--spacing-3xl)' }}>
              {stats.map((stat, index) => (
                <Stat key={index}>
                  <span className="value">{stat.value}{stat.note && <ProofMark />}</span>
                  <span className="label">{stat.label}</span>
                </Stat>
              ))}
            </HighlightStats>
            {stats.filter((stat) => stat.note).map((stat) => (
              <StatNote key={stat.label} data-stat-note style={{ marginBottom: 'var(--spacing-3xl)' }}>*{stat.note}</StatNote>
            ))}
            </div>
          </ScrollAnimation>
        )}

        {features.length > 0 && (
        <ContentSection>
          <ScrollAnimation animation="slideInFromBottom" delay={200}>
            <SectionHeader>
              <h2>{featuresHeading?.title ?? 'Service Offerings'}</h2>
              <p>{featuresHeading?.description ?? 'Practical solutions, tailored to your business goals.'}</p>
            </SectionHeader>
          </ScrollAnimation>

          <ContentGrid $columns={features.length === 2 || features.length === 4 ? 2 : 3}>
            {features.map((feature, index) => (
              <ScrollAnimation key={index} animation="fadeIn" delay={300 + index * 100}>
                <FeatureCard>
                  <div className="icon">{feature.icon}</div>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                  {feature.benefits && (
                    <ul className="benefits">
                      {feature.benefits.map((benefit, i) => (
                        <li key={i}>{benefit}</li>
                      ))}
                    </ul>
                  )}
                </FeatureCard>
              </ScrollAnimation>
            ))}
          </ContentGrid>
        </ContentSection>
        )}

        {process && (
          <ProcessSection>
            <ScrollAnimation animation="slideInFromBottom" delay={100}>
              <SectionHeader>
                <h2>{process.title}</h2>
                <p>{process.description}</p>
              </SectionHeader>
            </ScrollAnimation>

            <ProcessTimeline>
              {process.steps.map((step, index) => (
                <ProcessStep key={index} $index={index}>
                  <StepNumber>{index + 1}</StepNumber>
                  <StepContent>
                    <h3>{step.title}</h3>
                    <p>{step.description}</p>
                  </StepContent>
                </ProcessStep>
              ))}
            </ProcessTimeline>
          </ProcessSection>
        )}

        <Flow>
          {children}

          {faqs && (
            <FAQ
              embedded
              heading={faqs.heading}
              subheading={faqs.intro}
              items={faqs.items}
              initialVisible={faqs.initialVisible}
              layout={banded ? 'split' : 'stacked'}
              eyebrow={faqs.eyebrow}
              aside={faqs.aside}
              onOpen={faqs.onOpen}
            />
          )}

          {banded ? (
            <CompactCTA ref={closingCtaRef} aria-labelledby="service-cta-heading">
              <div>
                <h2 id="service-cta-heading">{cta.title}</h2>
                <p>{cta.description}</p>
              </div>
              <CompactActions>
                <CtaAction {...cta} />
                {(cta.secondary || cta.footer) && (
                  <CompactExtras>
                    {cta.secondary && <Link to={cta.secondary.href} onClick={cta.secondary.onClick}>{cta.secondary.text}</Link>}
                    {cta.secondary && cta.footer && <span aria-hidden="true">&middot;</span>}
                    {cta.footer && <span>{cta.footer}</span>}
                  </CompactExtras>
                )}
              </CompactActions>
            </CompactCTA>
          ) : (
            <ScrollAnimation animation="scaleIn" delay={100}>
              <CTASection>
                <h2>{cta.title}</h2>
                <p>{cta.description}</p>
                <CtaAction {...cta} />
                {(cta.secondary || cta.footer) && (
                  <CTAExtras>
                    {cta.secondary && <Link to={cta.secondary.href} onClick={cta.secondary.onClick}>{cta.secondary.text}</Link>}
                    {cta.footer && <div>{cta.footer}</div>}
                  </CTAExtras>
                )}
              </CTASection>
            </ScrollAnimation>
          )}
        </Flow>
      </div>
      {banded && stickyCta && (
        <StickyCtaBar heroRef={heroCtaRef} endRef={closingCtaRef} {...stickyCta} />
      )}
    </PageWrapper>
  );
};

export default ServiceLayout;
